import { CircularProgress } from '@mui/material';
import { getMinorBreakDefaultsRequest, minorBreakExceptionRequests } from 'api/requests/contract-compliance-requests';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import { MinorBreakRule } from 'constants/MinorBreakRule';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__BREAK_HOURS_INTRO,
  CONTRACT_COMPLIANCE__EXCEPTION_BINARY_HINT,
  GENERAL__RETRY,
} from 'translations/constants';
import { IRuleException } from 'types/contract-compliance/IContractCompliance';
import ComplianceToasts from '../ComplianceToasts';
import { useRuleDefaultPanel } from '../hooks/useRuleDefaultPanel';
import { useRuleDefaults } from '../hooks/useRuleDefaults';
import { useRuleExceptionPanel } from '../hooks/useRuleExceptionPanel';
import { useRuleExceptions } from '../hooks/useRuleExceptions';
import { useToasts } from '../hooks/useToasts';
import PanelConfirmModals from '../PanelConfirmModals';
import RuleCard from '../RuleCard';
import RuleExceptionPanel from '../RuleExceptionPanel';
import { HintText, PanelErrorRow, SubAreaContainer } from '../styles';
import { RULE_NOTE_KEYS, RULE_SLUGS, RULE_TITLE_KEYS, RULES } from './constants';
import { minorBreakDefaultAdapter } from './defaultAdapter';
import MinorBreakDefaultPanel from './MinorBreakDefaultPanel';
import { formatRuleDefault } from './utils';

const TEST_ID = 'minor-breaks';

/** "Break hours for minors" sub-area: the two rule defaults, each with its own exception list. */
const BreakHoursForMinors = (): JSX.Element => {
  const [t] = useTranslation();
  const defaults = useRuleDefaults(getMinorBreakDefaultsRequest);
  // One list per rule; two explicit calls keep the hook order fixed.
  const continuousWorkExceptions = useRuleExceptions(MinorBreakRule.CONTINUOUS_WORK, minorBreakExceptionRequests);
  const legalWorkTimeExceptions = useRuleExceptions(MinorBreakRule.LEGAL_WORK_TIME, minorBreakExceptionRequests);
  const exceptionsByRule = {
    [MinorBreakRule.CONTINUOUS_WORK]: continuousWorkExceptions,
    [MinorBreakRule.LEGAL_WORK_TIME]: legalWorkTimeExceptions,
  };
  const { toasts, pushToast, dismissToast, pushRetryToast } = useToasts();

  const { reload: reloadDefaults } = defaults;
  const { reload: reloadContinuousWork } = continuousWorkExceptions;
  const { reload: reloadLegalWorkTime } = legalWorkTimeExceptions;
  /** The sub-area reloads with the latest data whenever a panel is saved or closed (side panel pattern). */
  const reloadAll = useCallback(() => {
    reloadDefaults();
    reloadContinuousWork();
    reloadLegalWorkTime();
  }, [reloadDefaults, reloadContinuousWork, reloadLegalWorkTime]);

  const panelCallbacks = {
    onSaved: (messageKey: string) => {
      reloadAll();
      pushToast('success', t(messageKey));
    },
    onClosed: reloadAll,
    onGone: (message: string) => {
      reloadAll();
      pushToast('error', message);
    },
    onStale: reloadAll,
    onError: (message: string) => pushToast('error', message),
  };
  const defaultPanel = useRuleDefaultPanel(minorBreakDefaultAdapter, panelCallbacks);
  const exceptionPanel = useRuleExceptionPanel(minorBreakExceptionRequests, false, {
    ...panelCallbacks,
    onRemoveFailed: (message: string, retry: () => void) => pushRetryToast(message, retry, `${TEST_ID}-remove-retry`),
  });

  const { open: openDefault } = defaultPanel;
  const { openAdd, openEdit } = exceptionPanel;
  const onEditException = useCallback((row: IRuleException) => openEdit(row), [openEdit]);

  const renderBody = () => {
    if (defaults.loadErrorMessage && !defaults.hasLoaded) {
      return (
        <PanelErrorRow data-testid={`${TEST_ID}-load-error`}>
          <ErrorBar message={defaults.loadErrorMessage} />
          <UPButton text={t(GENERAL__RETRY)} onClick={defaults.reload} outlined testId={`${TEST_ID}-load-retry`} />
        </PanelErrorRow>
      );
    }
    if (!defaults.hasLoaded) return <CircularProgress data-testid={`${TEST_ID}-loading`} />;
    return RULES.map(rule => {
      const value = defaults.defaults[rule];
      return (
        <RuleCard
          key={rule}
          title={t(RULE_TITLE_KEYS[rule])}
          note={t(RULE_NOTE_KEYS[rule])}
          defaultText={value ? formatRuleDefault(t, value) : undefined}
          testIdPrefix={`${TEST_ID}-${RULE_SLUGS[rule]}`}
          exceptions={exceptionsByRule[rule]}
          onEditDefault={() => openDefault(rule)}
          onAddException={() => openAdd(rule)}
          onEditException={onEditException}
        />
      );
    });
  };

  return (
    <SubAreaContainer data-testid={TEST_ID}>
      <HintText data-testid={`${TEST_ID}-intro`}>{t(CONTRACT_COMPLIANCE__BREAK_HOURS_INTRO)}</HintText>
      {renderBody()}

      <MinorBreakDefaultPanel panel={defaultPanel} />
      <RuleExceptionPanel
        panel={exceptionPanel}
        title={exceptionPanel.rule ? t(RULE_TITLE_KEYS[exceptionPanel.rule as MinorBreakRule]) : ''}
        testIdPrefix={`${TEST_ID}-exception`}
        hintKey={CONTRACT_COMPLIANCE__EXCEPTION_BINARY_HINT}
      />
      <PanelConfirmModals defaultPanel={defaultPanel} exceptionPanel={exceptionPanel} testIdPrefix={TEST_ID} />
      <ComplianceToasts toasts={toasts} onDismiss={dismissToast} />
    </SubAreaContainer>
  );
};

export default BreakHoursForMinors;
